import React from 'react';
import { Paperclip, Upload } from 'lucide-react';
import { CustomerCustomAttachment } from '../../../shared/types';

interface AttachmentSectionProps {
  attachments: CustomerCustomAttachment[];
  customerName: string;
  uploadedFiles: Record<string, File>;
  onFileSelect: (attachmentId: string, file: File) => void;
  onFileRemove: (attachmentId: string) => void;
  // Neue Props für Haupt-PDF Integration
  mainPdfFile?: File | null;
  onMainPdfSelect: (file: File) => void;
  onMainPdfRemove: () => void;
  versandmethode: 'email' | 'duo' | 'kein_versand' | 'xtax_portal';
  mandantennummer?: string;
  month: number;
  year: number;
  abrechnungsmethode?: 'lodas' | 'lug';
}

export const AttachmentSection: React.FC<AttachmentSectionProps> = ({
  attachments,
  customerName,
  uploadedFiles,
  onFileSelect,
  onFileRemove,
  mainPdfFile,
  onMainPdfSelect,
  onMainPdfRemove,
  versandmethode,
  mandantennummer,
  month,
  year,
  abrechnungsmethode
}) => {
  const requiredAttachments = attachments.filter(att => att.is_required);
  const hasMainPdf = versandmethode === 'email';
  const hasAnyUploads = hasMainPdf || requiredAttachments.length > 0;

  // Wenn keine Uploads erforderlich sind, nichts anzeigen
  if (!hasAnyUploads) {
    return null;
  }

  // Erstelle virtuelle Haupt-PDF Attachment zur Laufzeit
  const createVirtualMainAttachment = (): CustomerCustomAttachment => ({
    id: 'main-pdf',
    customer_id: '', // Nicht relevant für virtuelle Attachment
    filename: 'Monatsabschlusspaket',
    description: `Mitarbeiter- und Mandantenauswertungen aus DATEV ${abrechnungsmethode === 'lodas' ? 'LODAS' : abrechnungsmethode === 'lug' ? 'LuG' : 'Abrechnungssoftware'}`,
    file_type: 'pdf',
    is_required: true,
    is_active: true,
    sort_order: -1, // Damit es oben steht
    created_at: '',
    updated_at: ''
  });

  // Validierungsfunktion für alle Dateitypen
  const validateFileName = (file: File, fileType: CustomerCustomAttachment['file_type']): { isValid: boolean; error?: string } => {
    // Bei LODAS oder fehlender Mandantennummer keine Validierung
    if (!mandantennummer || abrechnungsmethode === 'lodas') {
      return { isValid: true };
    }

    // Fülle Mandantennummer mit führenden Nullen auf 5 Stellen auf
    const paddedMandantennummer = mandantennummer.padStart(5, '0');
    const expectedPrefix = `${paddedMandantennummer}_${String(month).padStart(2, '0')}_${year}`;
    
    // Entferne die Dateiendung
    const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
    
    // Prüfe ob der Dateiname mit dem erwarteten Prefix beginnt
    if (!fileNameWithoutExtension.startsWith(expectedPrefix)) {
      const expectedExtension = getExpectedExtension(fileType);
      return {
        isValid: false,
        error: `Der Dateiname muss mit "${expectedPrefix}" beginnen (z.B. ${expectedPrefix}_zusatzinfo${expectedExtension})`
      };
    }

    return { isValid: true };
  };

  const getExpectedExtension = (fileType: CustomerCustomAttachment['file_type']): string => {
    const extensionMap: Record<CustomerCustomAttachment['file_type'], string> = {
      'pdf': '.pdf',
      'doc': '.doc',
      'docx': '.docx',
      'xlsx': '.xlsx',
      'xls': '.xls',
      'txt': '.txt',
      'jpg': '.jpg',
      'jpeg': '.jpg',
      'png': '.png',
      'xml': '.xml',
      'zip': '.zip'
    };
    return extensionMap[fileType] || '';
  };

  // Alle Attachments kombinieren (virtuelle Haupt-PDF + Custom Attachments)
  const allAttachments = [
    ...(hasMainPdf ? [createVirtualMainAttachment()] : []),
    ...requiredAttachments
  ];

  return (
    <div style={{ 
      marginBottom: '20px',
      padding: '16px',
      backgroundColor: '#FEF3E2',
      borderRadius: '8px',
      border: '1px solid #F59E0B',
      maxWidth: '800px'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px',
        marginBottom: '12px'
      }}>
        <Paperclip size={20} color="#D97706" />
        <h4 style={{ 
          margin: 0, 
          fontSize: '16px', 
          fontWeight: '600',
          color: '#92400E'
        }}>
          {hasMainPdf && requiredAttachments.length > 0 
            ? `Dateien für ${customerName}` 
            : hasMainPdf 
              ? `Dateien für ${customerName}` 
              : `Erforderliche Anhänge für ${customerName}`
          }
        </h4>
      </div>

      {/* Info Text */}
      <p style={{ 
        fontSize: '14px', 
        color: '#B45309', 
        margin: '0 0 16px 0',
        lineHeight: '1.4'
      }}>
        {hasMainPdf && requiredAttachments.length > 0 
          ? 'Folgende Dateien müssen hochgeladen werden:'
          : hasMainPdf 
            ? 'Bitte laden Sie die monatliche Abrechnung hoch:'
            : 'Folgende Dateien werden für diesen Kunden benötigt:'
        }
      </p>

      {/* Upload Items */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {allAttachments.map((attachment) => {
          const isMainPdf = attachment.id === 'main-pdf';
          const uploadedFile = isMainPdf 
            ? mainPdfFile 
            : uploadedFiles[attachment.id];
            
          const onFileSelect = isMainPdf 
            ? onMainPdfSelect 
            : (file: File) => handleAttachmentSelect(attachment.id, file);
            
          const onFileRemove = isMainPdf 
            ? onMainPdfRemove 
            : () => handleAttachmentRemove(attachment.id);

          return (
            <AttachmentUploadItem
              key={attachment.id}
              attachment={attachment}
              uploadedFile={uploadedFile || undefined}
              onFileSelect={onFileSelect}
              onFileRemove={onFileRemove}
              mandantennummer={mandantennummer}
              month={month}
              year={year}
              abrechnungsmethode={abrechnungsmethode}
              onValidateFileName={validateFileName}
            />
          );
        })}
      </div>

      {/* Status Summary */}
      <div style={{ 
        marginTop: '16px',
        padding: '12px',
        backgroundColor: '#FDF4E6',
        borderRadius: '6px',
        border: '1px solid #FDE68A',
        fontSize: '13px',
        color: '#92400E',
        fontWeight: '500'
      }}>
        {(() => {
          const totalRequired = (hasMainPdf ? 1 : 0) + requiredAttachments.length;
          const totalUploaded = (mainPdfFile ? 1 : 0) + Object.keys(uploadedFiles).length;
          
          if (totalUploaded === totalRequired) {
            return totalRequired === 1 
              ? '✅ Datei wurde hochgeladen'
              : '✅ Alle erforderlichen Dateien wurden hochgeladen';
          } else {
            return `⚠️ ${totalRequired - totalUploaded} von ${totalRequired} Dateien fehlen noch`;
          }
        })()}
      </div>
    </div>
  );

  // Handler für Attachment-Auswahl 
  function handleAttachmentSelect(attachmentId: string, file: File) {
    onFileSelect(attachmentId, file);
  }

  // Handler für Attachment-Entfernung
  function handleAttachmentRemove(attachmentId: string) {
    onFileRemove(attachmentId);
  }
};

interface AttachmentUploadItemProps {
  attachment: CustomerCustomAttachment;
  uploadedFile?: File;
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
  mandantennummer?: string;
  month: number;
  year: number;
  abrechnungsmethode?: 'lodas' | 'lug';
  onValidateFileName: (file: File, fileType: CustomerCustomAttachment['file_type']) => { isValid: boolean; error?: string };
}

const AttachmentUploadItem: React.FC<AttachmentUploadItemProps> = ({
  attachment,
  uploadedFile,
  onFileSelect,
  onFileRemove,
  mandantennummer,
  month,
  year,
  abrechnungsmethode,
  onValidateFileName
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [validationError, setValidationError] = React.useState<string | null>(null);

  const getFileTypeDisplay = (fileType: string): string => {
    const typeMap: Record<string, string> = {
      'pdf': 'PDF',
      'doc': 'Word',
      'docx': 'Word',
      'xlsx': 'Excel',
      'xls': 'Excel',
      'txt': 'Text',
      'jpg': 'Bild',
      'jpeg': 'Bild',
      'png': 'Bild',
      'xml': 'XML',
      'zip': 'ZIP-Archiv'
    };
    return typeMap[fileType] || fileType.toUpperCase();
  };

  const getAcceptedTypes = (fileType: string): string => {
    const typeMap: Record<string, string> = {
      'pdf': '.pdf',
      'doc': '.doc',
      'docx': '.docx',
      'xlsx': '.xlsx',
      'xls': '.xls',
      'txt': '.txt',
      'jpg': '.jpg,.jpeg',
      'jpeg': '.jpg,.jpeg',
      'png': '.png',
      'xml': '.xml',
      'zip': '.zip'
    };
    return typeMap[fileType] || '';
  };

  const handleFileSelect = (file: File) => {
    // Validiere Dateiname
    const validation = onValidateFileName(file, attachment.file_type);
    if (!validation.isValid) {
      setValidationError(validation.error || 'Ungültiger Dateiname');
      return;
    }

    // Validierung erfolgreich
    setValidationError(null);
    onFileSelect(file);
  };

  const getExpectedFileName = (): string => {
    if (!mandantennummer || abrechnungsmethode === 'lodas') {
      return 'beliebiger_name' + getAcceptedTypes(attachment.file_type).split(',')[0];
    }
    // Fülle Mandantennummer mit führenden Nullen auf 5 Stellen auf
    const paddedMandantennummer = mandantennummer.padStart(5, '0');
    const extension = getAcceptedTypes(attachment.file_type).split(',')[0];
    return `${paddedMandantennummer}_${String(month).padStart(2, '0')}_${year}_(...)${extension}`;
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      gap: '8px'
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px',
        padding: '12px',
        backgroundColor: 'white',
        borderRadius: '6px',
        border: '1px solid #F3F4F6'
      }}>
        {/* File Info */}
        <div style={{ flex: 1 }}>
          <div style={{ 
            fontSize: '14px', 
            fontWeight: '500',
            color: '#92400E',
            marginBottom: '2px'
          }}>
            {attachment.filename}
          </div>
          
          {attachment.description && (
            <div style={{ 
              fontSize: '12px', 
              color: '#B45309',
              marginBottom: '4px'
            }}>
              {attachment.description}
            </div>
          )}

          <div style={{ 
            fontSize: '12px', 
            color: '#A3A3A3'
          }}>
            Typ: {getFileTypeDisplay(attachment.file_type)}
          </div>

          {/* Erwarteter Dateiname Hinweis */}
          {mandantennummer && abrechnungsmethode !== 'lodas' && (
            <div style={{ 
              fontSize: '11px', 
              color: '#6B7280',
              marginTop: '4px',
              fontStyle: 'italic'
            }}>
              Erwarteter Dateiname: {getExpectedFileName()}
            </div>
          )}
        </div>

        {/* Upload Status & Button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {uploadedFile ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ 
                fontSize: '12px', 
                color: '#059669',
                fontWeight: '500'
              }}>
                ✅ {uploadedFile.name}
              </div>
              <button
                onClick={onFileRemove}
                style={{
                  fontSize: '12px',
                  color: '#DC2626',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Entfernen
              </button>
            </div>
          ) : (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept={getAcceptedTypes(attachment.file_type)}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                }}
                style={{ display: 'none' }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  backgroundColor: '#F59E0B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                <Upload size={14} />
                Hochladen
              </button>
            </>
          )}
        </div>
      </div>

      {/* Validation Error */}
      {validationError && (
        <div style={{
          fontSize: '12px',
          color: '#DC2626',
          backgroundColor: '#FEF2F2',
          padding: '8px',
          borderRadius: '4px',
          border: '1px solid #FECACA'
        }}>
          {validationError}
        </div>
      )}
    </div>
  );
};